import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Languages, X, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { findSource, getLang, isReview, REVIEW_KEY, LANGS } from "@/i18n";

export const exitReview = () => { localStorage.removeItem(REVIEW_KEY); window.location.reload(); };
export const enterReview = () => { localStorage.setItem(REVIEW_KEY, "1"); window.location.reload(); };

export const ReviewLayer = () => {
  const lang = getLang();
  const on = lang !== "en" && isReview();
  const [item, setItem] = useState(null);
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("review") === "1" && !isReview() && lang !== "en") enterReview();
  }, [lang]);

  useEffect(() => {
    if (!on) return undefined;
    document.body.classList.add("review-mode");
    const onClick = (e) => {
      if (e.target.closest("[data-review-ui],[role=dialog]")) return;
      const src = findSource(e.target);
      if (!src) return;
      e.preventDefault();
      e.stopPropagation();
      setItem(src); setText(src.current); setNote("");
    };
    document.addEventListener("click", onClick, true);
    return () => { document.removeEventListener("click", onClick, true); document.body.classList.remove("review-mode"); };
  }, [on]);

  if (!on) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await http.post("/translations/suggest", { lang, kind: item.kind, key: item.key, source: item.source, current: item.current, suggestion: text, note: note || null, page: window.location.pathname });
      toast.success("Thank you! Your suggestion was sent for review.");
      setItem(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the suggestion");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div data-review-ui className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] rounded-full bg-[#1C242B] text-white shadow-xl px-5 py-3 flex items-center gap-3 text-sm" data-testid="review-mode-banner">
        <Languages size={16} className="text-[#F0A584]" />
        <span>Review mode ({LANGS.find(([k]) => k === lang)?.[1]}): click any text to suggest a better translation</span>
        <button onClick={exitReview} data-testid="review-mode-exit-btn" className="ml-1 rounded-full bg-white/10 hover:bg-white/20 p-1.5" aria-label="Exit review"><X size={14} /></button>
      </div>
      <Dialog open={!!item} onOpenChange={(o) => !o && setItem(null)}>
        <DialogContent className="max-w-lg bg-[#FAF8F5]" data-testid="review-suggest-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Suggest a better translation</DialogTitle>
            <DialogDescription>Volunteer suggestions are reviewed before they go live.</DialogDescription>
          </DialogHeader>
          {item && (
            <div className="grid gap-4">
              <div><p className="text-xs font-mono-ui text-slate-500">ORIGINAL (ENGLISH)</p><p className="mt-1 text-sm" dir="ltr" data-no-translate data-testid="review-source-text">{item.source}</p></div>
              <div><p className="text-xs font-mono-ui text-slate-500">CURRENT</p><p className="mt-1 text-sm text-slate-600" data-no-translate data-testid="review-current-text">{item.current}</p></div>
              <div>
                <p className="text-xs font-mono-ui text-slate-500 mb-1">YOUR SUGGESTION</p>
                <textarea data-testid="review-suggestion-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000}
                  className="w-full rounded-xl border border-[#1C242B]/15 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D96B43]/40" />
              </div>
              <input data-testid="review-note-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Optional note (why this is better)"
                className="rounded-full border border-[#1C242B]/15 bg-white px-4 py-2 text-sm" />
              <button data-testid="review-submit-btn" onClick={submit} disabled={busy || !text.trim() || text.trim() === item.current}
                className="inline-flex justify-center items-center gap-2 rounded-full bg-[#D96B43] text-white px-5 py-3 text-sm font-semibold hover:bg-[#C05630] disabled:opacity-50 transition-colors">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send suggestion
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
